<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { Download, Loader2, CheckCircle2, AlertCircle, AlertTriangle, FileText, Lock, Info } from "lucide-vue-next";

  import { useSurveyDataImportStore } from "@/stores/surveyDataImport";
  import { useAccountsStore } from "@/stores/accounts";
  import { useContractsStore } from "@/stores/contracts";
  import { useCurrentUserStore } from "@/stores/currentUser";
  import { NO_CONTRACT_VALUE, QUANTITATIVE_RESPONDENTS_PER_CREDIT } from "@/stores/survey/setup/surveyCredits";
  import { creditsLabel } from "@/utils/credits/creditRules";
  import { getLanguageOptions } from "@/utils/constants/languages";
  import type { Contract } from "@/types";
  import { surveyDataImportSchema, MAX_FILE_SIZE_BYTES, type SurveyDataImportForm, type FormatValue } from "@schemas/surveyDataImport";

  import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
  import { FormItem, FormField, FormControl, FormLabel, FormMessage } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";
  import { Progress } from "@/components/ui/progress";
  import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
  import { FileDropzone } from "@/components/episto-ui/file-dropzone";

  // Imported surveys are quantitative data uploads, so they bill at the standard
  // quantitative rate (1 credit per 1,000 respondents), never the AI-chat rate.
  const props = withDefaults(defineProps<{ open?: boolean }>(), {
    open: false,
  });

  const emit = defineEmits<{ "update:open": [value: boolean] }>();

  const EXCEL_TEMPLATE_URL =
    "https://docs.google.com/spreadsheets/d/1V2EPGXNVb_lbPjM6eEd9KYF_NS9Eb-MyXm8aPZ0ZV-s/export?format=xlsx";

  const FORMAT_CONFIG = {
    xlsx: { accept: ".xlsx", hint: ".xlsx files only" },
    sav: { accept: ".sav", hint: ".sav files only" },
  };

  const LANGUAGE_OPTIONS = Object.values(getLanguageOptions());

  const importStore = useSurveyDataImportStore();
  const { status: validationStatus, result: validationResult, submitError } = storeToRefs(importStore);

  const accountsStore = useAccountsStore();
  const { availableAccountsOptions } = storeToRefs(accountsStore);

  const contractsStore = useContractsStore();
  const currentUserStore = useCurrentUserStore();
  const { isAdmin, isSale, userAccountIds } = storeToRefs(currentUserStore);

  const canSelectNoContract = computed(() => isAdmin.value || isSale.value);

  const accountContracts = ref<Contract[]>([]);

  let contractsRequestToken = 0;

  const defaultFormValues = (): SurveyDataImportForm => ({
    name: "",
    format: "xlsx",
    language: "fr",
    account_id: "",
    contract_id: "",
  });

  const { handleSubmit, values, resetForm, setFieldValue } = useForm({
    validationSchema: toTypedSchema(surveyDataImportSchema),
    initialValues: defaultFormValues(),
  });

  const file = ref<File | null>(null);
  const fileError = ref("");

  const currentFormat = computed<FormatValue>(() => values.format ?? "xlsx");
  const formatConfig = computed(() => FORMAT_CONFIG[currentFormat.value]);
  const isExcelMode = computed(() => currentFormat.value === "xlsx");

  const isSubmitting = computed(() => validationStatus.value === "submitting");

  // --- Contract & credit preview -------------------------------------------

  const contractOptions = computed(() => {
    const options = accountContracts.value.map((c) => ({
      label: `${c.name} (${c.credits_remaining} credits remaining)`,
      value: String(c.id),
    }));
    if (canSelectNoContract.value) {
      options.unshift({ label: "No contract (admin only)", value: NO_CONTRACT_VALUE });
    }
    return options;
  });

  const selectedContract = computed(() =>
    accountContracts.value.find((c) => String(c.id) === values.contract_id),
  );

  const respondentCount = computed(() =>
    validationStatus.value === "valid" ? (validationResult.value?.respondent_count ?? 0) : 0,
  );

  const creditsNeeded = computed(() => Math.ceil(respondentCount.value / QUANTITATIVE_RESPONDENTS_PER_CREDIT));

  const hasNoContractSelected = computed(() => values.contract_id === NO_CONTRACT_VALUE);
  const isContractMissing = computed(() => !values.contract_id && !canSelectNoContract.value);

  // Enough credits left on the chosen contract to absorb the whole import?
  const exceedsRemaining = computed(() => {
    if (!selectedContract.value) return false;
    return creditsNeeded.value > selectedContract.value.credits_remaining;
  });

  // Show the preview as soon as the file is validated and a contract decision is made.
  const showCreditPreview = computed(
    () => validationStatus.value === "valid" && (Boolean(selectedContract.value) || hasNoContractSelected.value),
  );

  const remainingAfterImport = computed(() =>
    selectedContract.value ? Math.max(0, selectedContract.value.credits_remaining - creditsNeeded.value) : 0,
  );

  const consumedPercentageAfter = computed(() => {
    const total = selectedContract.value?.total_credits ?? 0;
    if (!total) return 0;
    return Math.min(100, Math.round(((total - remainingAfterImport.value) / total) * 100));
  });

  const isImportDisabled = computed(
    () =>
      validationStatus.value !== "valid" ||
      !values.name?.trim() ||
      !values.language ||
      !values.account_id ||
      isContractMissing.value ||
      // Clients can't exceed their contract's credits; Episto staff may over-allocate.
      (exceedsRemaining.value && !canSelectNoContract.value) ||
      isSubmitting.value,
  );

  watch(
    () => values.format,
    () => {
      file.value = null;
      fileError.value = "";
      importStore.reset();
    },
  );

  watch(file, async (f) => {
    if (f) fileError.value = "";
    await importStore.validate(f, currentFormat.value);
  });

  watch(
    () => values.account_id,
    async (accountId) => {
      // Clear up front so the previous account's contracts never linger mid-fetch.
      const requestToken = ++contractsRequestToken;
      accountContracts.value = [];
      setFieldValue("contract_id", "");

      if (!accountId) return;

      const contracts = await contractsStore.fetchActiveForAccount(accountId);
      // Drop stale responses: the account changed again while this was fetching.
      if (requestToken !== contractsRequestToken) return;
      accountContracts.value = contracts;

      if (canSelectNoContract.value) {
        setFieldValue("contract_id", NO_CONTRACT_VALUE);
      } else if (contracts.length) {
        const soonest = [...contracts].sort(
          (a, b) => new Date(a.ends_at).getTime() - new Date(b.ends_at).getTime(),
        )[0];
        setFieldValue("contract_id", String(soonest.id));
      }
    },
  );

  watch(
    () => props.open,
    async (open) => {
      if (!open) {
        if (importStore.signedBlobId) importStore.discard();
        return;
      }

      file.value = null;
      fileError.value = "";
      importStore.reset();
      resetForm({ values: defaultFormValues() });

      if (!availableAccountsOptions.value.length) {
        await accountsStore.fetch();
      }
      // Default to the user's own account (not just the first alphabetically), else the first available.
      const ownAccountIds = new Set(userAccountIds.value ?? []);
      const defaultAccount =
        availableAccountsOptions.value.find((o) => ownAccountIds.has(o.value)) ?? availableAccountsOptions.value[0];
      setFieldValue("account_id", defaultAccount?.value ?? "");
    },
  );

  const close = () => emit("update:open", false);

  const downloadTemplate = () => {
    window.open(EXCEL_TEMPLATE_URL, "_blank", "noopener,noreferrer");
  };

  const onSubmit = handleSubmit(async (submitted) => {
    if (!file.value) {
      fileError.value = "Please select a file";
      return;
    }
    if (validationStatus.value !== "valid") return;

    const ok = await importStore.createSurvey({
      name: submitted.name,
      language: submitted.language,
      accountId: submitted.account_id,
      format: submitted.format,
      contractId:
        submitted.contract_id && submitted.contract_id !== NO_CONTRACT_VALUE ? Number(submitted.contract_id) : null,
    });
    if (ok) emit("update:open", false);
  });
</script>

<template>
  <Dialog
    :open="props.open"
    @update:open="emit('update:open', $event)">
    <DialogContent class="flex max-h-[90vh] flex-col sm:max-w-lg">
      <DialogHeader class="shrink-0">
        <DialogTitle>Import survey data</DialogTitle>
        <DialogDescription>
          Import respondent data from an external file to create a new survey.
        </DialogDescription>
      </DialogHeader>
      <form
        class="flex min-h-0 flex-1 flex-col gap-5"
        autocomplete="off"
        @submit.prevent="onSubmit">
        <div class="flex-1 space-y-5 overflow-y-auto pr-1">
        <FormField
          v-slot="{ componentField }"
          name="name">
          <FormItem>
            <FormLabel>Survey name</FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                autocomplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>
        <div class="grid grid-cols-2 gap-4">
          <FormField name="language">
            <FormItem>
              <FormLabel>Language</FormLabel>
              <FormControl>
                <Select
                  :model-value="values.language"
                  :options="LANGUAGE_OPTIONS"
                  filterable
                  placeholder="Select a language"
                  @update:model-value="(v) => setFieldValue('language', v as string)" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
          <FormField
            v-slot="{ componentField }"
            name="account_id">
            <FormItem>
              <FormLabel>Account</FormLabel>
              <FormControl>
                <Select
                  v-bind="componentField"
                  :options="availableAccountsOptions"
                  filterable
                  placeholder="Select an account" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <FormField name="contract_id">
          <FormItem>
            <FormLabel>Contract</FormLabel>
            <FormControl>
              <Select
                :model-value="values.contract_id"
                :options="contractOptions"
                filterable
                placeholder="Select a contract"
                @update:model-value="(v) => setFieldValue('contract_id', v as string)" />
            </FormControl>
            <div
              v-if="isContractMissing"
              class="flex items-start gap-3 rounded-lg border border-status-warning-border bg-status-warning-soft px-4 py-3 text-sm text-status-warning-strong">
              <AlertTriangle class="h-5 w-5 text-status-warning-strong shrink-0 mt-0.5" />
              <div class="space-y-0.5">
                <p class="font-medium">No active contract for this account</p>
                <p class="text-status-warning-strong">
                  Imported respondents consume credits, so an active contract is required. Contact your CSM to set one
                  up.
                </p>
              </div>
            </div>
            <div
              v-else
              class="flex items-start gap-3 rounded-lg border border-status-info-border bg-status-info-soft px-4 py-3 text-sm text-status-info-strong">
              <Info class="h-5 w-5 text-status-info-strong shrink-0 mt-0.5" />
              <p>Imported respondents consume credits at the same rate as collected ones (1 credit / 1,000 respondents).</p>
            </div>
          </FormItem>
        </FormField>
        <FormField
          v-slot="{ componentField }"
          name="format">
          <FormItem>
            <FormLabel>Format</FormLabel>
            <FormControl>
              <Tabs
                v-bind="componentField"
                default-value="xlsx"
                class="w-full">
                <TabsList class="w-full">
                  <TabsTrigger value="xlsx">Excel (.xlsx)</TabsTrigger>
                  <TabsTrigger value="sav">SPSS (.sav)</TabsTrigger>
                </TabsList>
              </Tabs>
            </FormControl>
          </FormItem>
        </FormField>
        <FormItem v-if="isExcelMode">
          <Label>1. Download &amp; fill the template</Label>
          <Button
            type="button"
            variant="outline"
            class="w-full justify-start font-normal"
            @click="downloadTemplate">
            <Download class="mr-2 h-4 w-4" />
            Download Excel template
          </Button>
        </FormItem>
        <FormItem>
          <Label>{{ isExcelMode ? "2. Upload your file" : "Upload your file" }}</Label>
          <FileDropzone
            v-model="file"
            :accept="formatConfig.accept"
            :hint="formatConfig.hint"
            :max-size="MAX_FILE_SIZE_BYTES" />
          <p
            v-if="fileError"
            class="text-sm text-destructive">
            {{ fileError }}
          </p>
          <div
            v-if="validationStatus === 'uploading' || validationStatus === 'validating'"
            class="flex items-center gap-2 rounded-md bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
            <Loader2 class="h-4 w-4 animate-spin" />
            <span>{{ validationStatus === "uploading" ? "Uploading…" : "Validating…" }}</span>
          </div>
          <div
            v-else-if="validationStatus === 'valid'"
            class="flex items-center gap-2 rounded-md border border-status-success-border bg-status-success-soft px-3 py-2 text-sm text-status-success-strong">
            <CheckCircle2 class="h-4 w-4 shrink-0" />
            <span>{{ validationResult?.respondent_count }} respondents, {{ validationResult?.question_count }} questions</span>
          </div>
          <div
            v-else-if="validationStatus === 'invalid'"
            class="rounded-md border border-status-error-border bg-status-error-soft px-3 py-3 text-sm text-status-error-strong">
            <div class="mb-1 flex items-center gap-2 font-semibold">
              <AlertCircle class="h-4 w-4 shrink-0" />
              Validation errors
            </div>
            <ul class="max-h-48 space-y-1 overflow-y-auto overflow-x-hidden">
              <li
                v-for="(err, i) in validationResult?.errors ?? []"
                :key="i"
                class="whitespace-pre-wrap break-words">
                - {{ err }}
              </li>
            </ul>
          </div>
        </FormItem>

        <!-- Credit consumption preview: appears once the file is validated and a contract decision is made -->
        <template v-if="showCreditPreview">
          <!-- Admin opted out of a contract -->
          <div
            v-if="hasNoContractSelected"
            class="flex items-start gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
            <Info class="h-4 w-4 shrink-0 mt-0.5" />
            <p>No contract selected. This import won't consume any credits.</p>
          </div>

          <!-- Not enough credits left on the contract -->
          <div
            v-else-if="exceedsRemaining"
            class="rounded-lg border border-status-warning-border bg-status-warning-soft px-4 py-3 text-sm text-status-warning-strong">
            <div class="flex items-start gap-2">
              <Lock class="h-5 w-5 text-status-warning-strong shrink-0 mt-0.5" />
              <div>
                <p class="font-medium">
                  This import needs {{ creditsNeeded }} {{ creditsLabel(creditsNeeded) }}, but only
                  {{ selectedContract?.credits_remaining }} remain on {{ selectedContract?.name }}.
                </p>
                <p class="text-status-warning-strong">
                  {{
                    canSelectNoContract
                      ? "Increase the contract's credits, or import without a contract."
                      : "Contact your CSM to allocate more credits to this contract."
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Enough credits: show what will be consumed -->
          <div
            v-else
            class="rounded-lg border border-status-success-border bg-status-success-soft px-4 py-3 space-y-2">
            <div class="flex items-start gap-2 text-sm text-status-success-strong">
              <FileText class="h-5 w-5 text-status-success-strong shrink-0 mt-0.5" />
              <p>
                This import contains <span class="font-semibold">{{ respondentCount }}</span> respondents and will
                consume <span class="font-semibold">{{ creditsNeeded }}</span>
                {{ creditsLabel(creditsNeeded) }} from
                <span class="font-semibold">{{ selectedContract?.name }}</span>.
              </p>
            </div>
            <div class="flex items-center gap-3 pl-7">
              <Progress
                :model-value="consumedPercentageAfter"
                class="h-2 flex-1 [&>div]:bg-status-success" />
              <span class="text-xs text-status-success-strong whitespace-nowrap">
                {{ remainingAfterImport }} / {{ selectedContract?.total_credits }} credit(s) will remain
              </span>
            </div>
          </div>
        </template>
          <p
            v-if="submitError"
            class="text-sm text-destructive">
            {{ submitError }}
          </p>
        </div>
        <DialogFooter class="shrink-0">
          <Button
            type="button"
            variant="outline"
            :disabled="isSubmitting"
            @click="close">
            Cancel
          </Button>
          <Button
            type="submit"
            :disabled="isImportDisabled">
            <Loader2
              v-if="isSubmitting"
              class="mr-2 h-4 w-4 animate-spin" />
            {{ isSubmitting ? "Starting…" : "Import" }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>
